/**
 * JobTypesEditor — tenant-owned job-type CRUD (DEC-140, Setup-Wizard Step 7).
 *
 * Flat EDIT form over the full 09.1 versioned contract (DEC-170). Creation is
 * a separate multi-step wizard (Plan 09.1-11); this form only edits. Writes go
 * to /v1/job-types (MANAGE_REFERENCE_DATA); on success both the picker + editor
 * caches are invalidated. New tenants start with zero rows, so the empty state
 * guides them to add their first type.
 *
 * The structural-change guardrail (JOBTYPE-12/17, DEC-170): editing any of
 * STRUCTURAL_FIELDS (ticketShape, tankCountMode, tankCountMax) forks a new
 * job-type version instead of mutating in place, once acknowledged. The
 * client detects this BEFORE submitting as UX; the server's 409
 * JOB_TYPE_VERSION_REQUIRED is the authoritative backstop (T-098-01).
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';
import { parseDollarsToCents } from '@lodex-pro/shared/money';
import { fieldStyle, FormRow } from '../sites-operators/form-bits';
import { useHaulerOperators } from '../sites-operators/hooks';
import {
  useJobTypesAdmin,
  useJobTypeEditImpact,
  changedStructuralFields,
  JOB_TYPES_QUERY_KEY,
  JOB_TYPES_ADMIN_QUERY_KEY,
  REQUIREMENT_LEVELS,
  REQUIREMENT_LABELS,
  PHOTO_REQUIREMENT_HELP,
  TICKET_SHAPES,
  TANK_COUNT_MODES,
  TANK_COUNT_MODE_LABELS,
  BILLING_METHODS,
  CAPTURE_METHODS,
  COMPLETION_LOCATIONS,
  type JobType,
  type RequirementLevel,
  type TicketShape,
  type TankCountMode,
} from '../jobs/use-job-types';
import { useJobTypeCustomers, useReplaceJobTypeCustomers } from '../jobs/use-job-type-customers';
import { useProducts } from '../jobs/use-products';
import { JobTypeWizard } from './JobTypeWizard';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** Human names for STRUCTURAL_FIELDS, used in the version-guardrail dialog. */
const STRUCTURAL_FIELD_LABELS: Record<string, string> = {
  ticketShape: 'ticket shape',
  tankCountMode: 'tank count mode',
  tankCountMax: 'tank count limit',
};

const subText: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid var(--color-border-primary)',
  marginBottom: '6px',
};
const btn: React.CSSProperties = {
  fontSize: '13px',
  padding: '6px 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-primary)',
  background: 'transparent',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  ...btn,
  background: 'var(--color-accent-blue)',
  borderColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const sectionHeading: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '13px',
  marginTop: '8px',
  borderTop: '1px solid var(--color-border-primary)',
  paddingTop: '8px',
};
const confirmBoxStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '10px',
  borderRadius: '6px',
  border: '1px solid var(--color-warning)',
  background: 'var(--color-bg-tertiary)',
};

/**
 * DEC-176 — the client mirror of the server unit-sync: when a default product is
 * linked, its unit becomes the job type's unit (read-only in the form). Clearing the
 * product ('') keeps the current (freely-editable) unit. The server re-derives this
 * authoritatively on save; this keeps the form consistent before submit. Pure +
 * exported for unit testing.
 */
export function syncedUnitForProduct(
  products: ReadonlyArray<{ id: string; unitOfMeasure: string }>,
  productId: string,
  currentUnit: string,
): string {
  if (productId === '') return currentUnit;
  const picked = products.find((p) => p.id === productId);
  return picked !== undefined ? picked.unitOfMeasure : currentUnit;
}

/** The editable form state (rate + tank-count-max held as input strings). */
interface FormState {
  code: string;
  label: string;
  billingMethod: string;
  rateDollars: string;
  unitOfMeasureCode: string;
  // DEC-176 — default product ('' = none) + require-product rule.
  defaultProductId: string;
  productRequirement: RequirementLevel;
  captureMethod: string;
  completionLocation: string;
  requireSignatureReason: boolean;
  isDefaultForDispatch: boolean;
  isActive: boolean;
  isServiceWork: boolean;
  ticketShape: TicketShape;
  tankCountMode: TankCountMode;
  tankCountMax: string;
  waterVolumeRequirement: RequirementLevel;
  oilVolumeRequirement: RequirementLevel;
  topGaugeRequirement: RequirementLevel;
  bottomGaugeRequirement: RequirementLevel;
  sealRequirement: RequirementLevel;
  colorCutRequirement: RequirementLevel;
  tankNameRequirement: RequirementLevel;
  driverCommentRequirement: RequirementLevel;
  photoRequirement: RequirementLevel;
  driverTimeRequirement: RequirementLevel;
  useCompanyPricing: boolean;
  disposalFeeEnabled: boolean;
}

const EMPTY_FORM: FormState = {
  code: '',
  label: '',
  billingMethod: 'PER_BBL',
  rateDollars: '',
  unitOfMeasureCode: 'BBL',
  defaultProductId: '',
  productRequirement: 'OPTIONAL',
  captureMethod: 'SINGLE_GAUGE',
  completionLocation: 'DISPOSAL_FACILITY',
  requireSignatureReason: false,
  isDefaultForDispatch: false,
  isActive: true,
  isServiceWork: false,
  ticketShape: 'TANK',
  tankCountMode: 'SINGLE',
  tankCountMax: '',
  waterVolumeRequirement: 'OPTIONAL',
  oilVolumeRequirement: 'OPTIONAL',
  topGaugeRequirement: 'OPTIONAL',
  bottomGaugeRequirement: 'OPTIONAL',
  sealRequirement: 'OPTIONAL',
  colorCutRequirement: 'OPTIONAL',
  tankNameRequirement: 'OPTIONAL',
  driverCommentRequirement: 'OPTIONAL',
  photoRequirement: 'OPTIONAL',
  driverTimeRequirement: 'OPTIONAL',
  useCompanyPricing: false,
  disposalFeeEnabled: false,
};

function fromJobType(jt: JobType): FormState {
  return {
    code: jt.code,
    label: jt.label,
    // Phase 12.1 (D-06) — defensively coerce a LINE_ITEM row's billing method to
    // PER_UNIT on load. Post-migration-0075 the DB CHECK makes any other value
    // impossible, but a pre-CHECK row could theoretically exist; coercing here
    // guarantees the locked read-only field never displays a stale invalid value.
    billingMethod: jt.ticketShape === 'LINE_ITEM' ? 'PER_UNIT' : jt.billingMethod,
    rateDollars: jt.defaultRateCents === null ? '' : (jt.defaultRateCents / 100).toString(),
    unitOfMeasureCode: jt.unitOfMeasureCode ?? '',
    defaultProductId: jt.defaultProductId ?? '',
    productRequirement: jt.productRequirement ?? 'OPTIONAL',
    captureMethod: jt.captureMethod,
    completionLocation: jt.completionLocation,
    requireSignatureReason: jt.requireSignatureReason,
    isDefaultForDispatch: jt.isDefaultForDispatch,
    isActive: jt.isActive,
    // D-05 — Service Work is NOT a parallel boolean; it reads off the
    // existing captureType discriminator. There is deliberately no second
    // column for this: two fields that can disagree about whether a job
    // type is service work is the failure mode this decision prevents.
    isServiceWork: jt.captureType === 'SERVICE_CALL',
    ticketShape: jt.ticketShape,
    tankCountMode: jt.tankCountMode ?? 'SINGLE',
    tankCountMax: jt.tankCountMax === null ? '' : jt.tankCountMax.toString(),
    waterVolumeRequirement: jt.waterVolumeRequirement ?? 'OPTIONAL',
    oilVolumeRequirement: jt.oilVolumeRequirement ?? 'OPTIONAL',
    topGaugeRequirement: jt.topGaugeRequirement ?? 'OPTIONAL',
    bottomGaugeRequirement: jt.bottomGaugeRequirement ?? 'OPTIONAL',
    sealRequirement: jt.sealRequirement ?? 'OPTIONAL',
    colorCutRequirement: jt.colorCutRequirement ?? 'OPTIONAL',
    tankNameRequirement: jt.tankNameRequirement ?? 'OPTIONAL',
    driverCommentRequirement: jt.driverCommentRequirement,
    photoRequirement: jt.photoRequirement,
    driverTimeRequirement: jt.driverTimeRequirement,
    useCompanyPricing: jt.useCompanyPricing,
    disposalFeeEnabled: jt.disposalFeeEnabled,
  };
}

/** Serialize form → API body for a PATCH. `code` is immutable after creation
 *  (creation now happens exclusively through JobTypeWizard's own POST body,
 *  Plan 09.1-11) and is never sent here. Deliberately does NOT send the four
 *  deprecated legacy booleans (sealRequired/requireWaterVolume/
 *  requireOilVolume/requireGaugeReadings) — the server mirror-writes them
 *  from the *_requirement enums (Plan 09.1-02); sending both would let the
 *  client disagree with the server's own mirror. */
function toBody(f: FormState): Record<string, unknown> {
  const trimmedRate = f.rateDollars.trim();
  // parseDollarsToCents reads the decimal DIGITS. The old
  // `Math.round(Number.parseFloat(r) * 100)` lost a cent on every half-cent rate —
  // `1.005` became $1.00, not $1.01 — because the field below is a free-text
  // `inputMode="decimal"` input with no decimal-place validation, so a third
  // decimal reaches here. (OperatorFormModal gates on /\.\d{1,2}/ and was safe.)
  const parsedRate = parseDollarsToCents(trimmedRate);
  const rateCents = parsedRate.ok ? parsedRate.cents : null;
  const isTank = f.ticketShape === 'TANK';
  const trimmedMax = f.tankCountMax.trim();
  const tankCountMax =
    isTank && f.tankCountMode === 'MAX_N' && trimmedMax !== ''
      ? Number.parseInt(trimmedMax, 10)
      : null;

  const body: Record<string, unknown> = {
    label: f.label.trim(),
    billingMethod: f.billingMethod,
    defaultRateCents: rateCents,
    unitOfMeasureCode: f.unitOfMeasureCode.trim() === '' ? null : f.unitOfMeasureCode.trim(),
    // DEC-176 — default product ('' → null); the server re-derives the unit from it
    // authoritatively, but we send our synced unit above so the fork's change
    // detection records both structural fields. productRequirement only matters when
    // no default product, but it is harmless to always send.
    defaultProductId: f.defaultProductId === '' ? null : f.defaultProductId,
    productRequirement: f.productRequirement,
    captureMethod: f.captureMethod,
    completionLocation: f.completionLocation,
    // D-05 — the toggle IS the control; captureType is never rendered raw.
    captureType: f.isServiceWork ? 'SERVICE_CALL' : 'HAUL',
    requireSignatureReason: f.requireSignatureReason,
    isDefaultForDispatch: f.isDefaultForDispatch,
    isActive: f.isActive,
    ticketShape: f.ticketShape,
    // D-03 — LINE_ITEM must carry NO tank-only field; the server CHECK
    // requires every tank column to be NULL when ticketShape is LINE_ITEM.
    tankCountMode: isTank ? f.tankCountMode : null,
    tankCountMax: isTank ? tankCountMax : null,
    waterVolumeRequirement: isTank ? f.waterVolumeRequirement : null,
    oilVolumeRequirement: isTank ? f.oilVolumeRequirement : null,
    topGaugeRequirement: isTank ? f.topGaugeRequirement : null,
    bottomGaugeRequirement: isTank ? f.bottomGaugeRequirement : null,
    sealRequirement: isTank ? f.sealRequirement : null,
    colorCutRequirement: isTank ? f.colorCutRequirement : null,
    tankNameRequirement: isTank ? f.tankNameRequirement : null,
    driverCommentRequirement: f.driverCommentRequirement,
    photoRequirement: f.photoRequirement,
    driverTimeRequirement: f.driverTimeRequirement,
    useCompanyPricing: f.useCompanyPricing,
    disposalFeeEnabled: f.disposalFeeEnabled,
  };
  return body;
}

/** State for the structural-change version-guardrail confirmation panel. */
interface ConfirmState {
  readonly changedFields: readonly string[];
  readonly jobCount: number;
  readonly version: number;
  readonly body: Record<string, unknown>;
}

export function JobTypesEditor(): React.JSX.Element {
  const opts = useApiOpts();
  const qc = useQueryClient();
  const { data, isLoading } = useJobTypesAdmin(opts);
  const jobTypes = data?.jobTypes ?? [];

  const [editingId, setEditingId] = React.useState<number | null>(null);
  // Plan 09.1-11 — Create opens the wizard; Edit opens the flat form below.
  const [wizardOpen, setWizardOpen] = React.useState(false);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [originalJobType, setOriginalJobType] = React.useState<JobType | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmState, setConfirmState] = React.useState<ConfirmState | null>(null);
  const [supersededConflict, setSupersededConflict] = React.useState(false);

  // JOBTYPE-12 preflight — jobCount/hasExistingTickets for the guardrail
  // dialog. staleTime:0 inside the hook — never masks the warning.
  const editImpact = useJobTypeEditImpact(opts, editingId);

  const invalidate = React.useCallback(async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: JOB_TYPES_QUERY_KEY }),
      qc.invalidateQueries({ queryKey: JOB_TYPES_ADMIN_QUERY_KEY }),
      // Prefix invalidation — covers every useJobTypesForCustomer(...) key.
      qc.invalidateQueries({ queryKey: ['job-types', 'catalog', 'customer'] }),
      // Prefix invalidation — covers every useJobTypeEditImpact(...) key.
      qc.invalidateQueries({ queryKey: ['job-types', 'edit-impact'] }),
    ]);
  }, [qc]);

  // Plan 09.1-11 Task 2 — Create opens the wizard now instead of the flat
  // form's own (now-removed) create branch. DEC-170: "Editing an existing
  // job type is a SEPARATE flat form, not a wizard re-run" — startEdit below
  // is unchanged.
  const startNew = (): void => {
    setWizardOpen(true);
  };
  const startEdit = (jt: JobType): void => {
    setForm(fromJobType(jt));
    setEditingId(jt.id);
    setOriginalJobType(jt);
    setConfirmState(null);
    setSupersededConflict(false);
    setError(null);
  };
  const cancel = (): void => {
    setEditingId(null);
    setOriginalJobType(null);
    setConfirmState(null);
    setSupersededConflict(false);
    setError(null);
  };

  /** Perform the actual PATCH. `acknowledgeVersion` re-issues a structural
   *  edit with the server's fork acknowledged (Task 2 Step B4). Creation no
   *  longer goes through this function (Plan 09.1-11 — JobTypeWizard owns
   *  its own POST /v1/job-types call). */
  const submitBody = async (
    body: Record<string, unknown>,
    acknowledgeVersion: boolean,
  ): Promise<void> => {
    setSaving(true);
    setError(null);
    setSupersededConflict(false);
    try {
      const finalBody = acknowledgeVersion ? { ...body, acknowledgeVersion: true } : body;
      const res = await authedFetch(opts, `/v1/job-types/${String(editingId)}`, {
        method: 'PATCH',
        body: JSON.stringify(finalBody),
      });
      if (!res.ok) {
        if (res.status === 409) {
          const errBody = (await res.json()) as {
            error?: { code?: string; message?: string };
            changedStructural?: readonly string[];
            jobCount?: number;
            hasExistingTickets?: boolean;
          };
          // The client's own structural check is UX only — the server's 409
          // is authoritative (T-098-01). Show the SAME dialog rather than a
          // generic failure, e.g. if the row changed under the user or the
          // client check missed a field.
          if (errBody.error?.code === 'JOB_TYPE_VERSION_REQUIRED') {
            setConfirmState({
              changedFields: errBody.changedStructural ?? [],
              jobCount: errBody.jobCount ?? 0,
              version: originalJobType?.version ?? 0,
              body,
            });
            return;
          }
          if (errBody.error?.code === 'JOB_TYPE_SUPERSEDED') {
            setSupersededConflict(true);
            setError(
              'This job type was changed by someone else. Reload to edit the current version.',
            );
            return;
          }
        }
        setError(
          res.status === 400
            ? 'Check your inputs — a job type with this code may already exist.'
            : `Save failed (${String(res.status)}).`,
        );
        return;
      }
      const resBody = (await res.json()) as { jobType: JobType };
      await invalidate();
      setConfirmState(null);
      if (acknowledgeVersion) {
        toast.success(`New version created (v${String(resBody.jobType.version)})`);
        // Reopen the editor on the NEW row — the old id is now superseded
        // and PATCHing it again would 409 JOB_TYPE_SUPERSEDED.
        startEdit(resBody.jobType);
      } else {
        toast.success('Job type saved');
        setEditingId(null);
        setOriginalJobType(null);
      }
    } catch {
      setError('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const save = async (): Promise<void> => {
    if (form.label.trim() === '') {
      setError('Label is required.');
      return;
    }
    // DEC-025 coherence, enforced client-side too (an inline message beats a
    // 500): bottom gauge cannot be stricter than top gauge.
    if (
      form.ticketShape === 'TANK' &&
      form.bottomGaugeRequirement === 'REQUIRED' &&
      form.topGaugeRequirement !== 'REQUIRED'
    ) {
      setError(
        "Bottom gauge cannot be required unless the top gauge is — gauging an empty tank's bottom is meaningless without the full-tank top reading.",
      );
      return;
    }

    const body = toBody(form);

    // Task 2 Step A — detect BEFORE submitting. Editing is the only path
    // through this form now (Plan 09.1-11), so originalJobType is always set
    // by the time save() can be called.
    if (originalJobType !== null) {
      const draft: Partial<JobType> = {
        ticketShape: body['ticketShape'] as TicketShape,
        tankCountMode: body['tankCountMode'] as TankCountMode | null,
        tankCountMax: body['tankCountMax'] as number | null,
        // DEC-176 — default product + unit are structural; include them so a change
        // to either triggers the same version-fork confirmation.
        defaultProductId: body['defaultProductId'] as string | null,
        unitOfMeasureCode: body['unitOfMeasureCode'] as string | null,
      };
      const changed = changedStructuralFields(originalJobType, draft);
      if (changed.length > 0) {
        // WARN-AND-PROCEED, not block — no request sent yet; Cancel changes
        // nothing, the form keeps the user's edits.
        setConfirmState({
          changedFields: changed,
          jobCount: editImpact.data?.jobCount ?? 0,
          version: originalJobType.version,
          body,
        });
        return;
      }
    }
    await submitBody(body, false);
  };

  const confirmNewVersion = (): void => {
    if (confirmState !== null) void submitBody(confirmState.body, true);
  };
  const cancelConfirm = (): void => {
    setConfirmState(null);
  };
  const reloadAfterConflict = async (): Promise<void> => {
    await invalidate();
    setEditingId(null);
    setOriginalJobType(null);
    setSupersededConflict(false);
    setError(null);
  };

  const setActive = async (jt: JobType, isActive: boolean): Promise<void> => {
    try {
      const res = await authedFetch(opts, `/v1/job-types/${String(jt.id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
      });
      if (!res.ok) {
        toast.error('Could not update. Please try again.');
        return;
      }
      await invalidate();
      toast.success(isActive ? 'Job type reactivated' : 'Job type deactivated');
    } catch {
      toast.error('Network error — please try again.');
    }
  };

  const activeCount = jobTypes.filter((jt) => jt.isActive).length;

  return (
    <div style={{ maxWidth: '560px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={subText}>
          {formatNumber(activeCount)} active job type{activeCount === 1 ? '' : 's'}
        </div>
        {editingId === null && !wizardOpen && (
          <button type="button" style={primaryBtn} onClick={startNew}>
            + New job type
          </button>
        )}
      </div>

      {wizardOpen && (
        <JobTypeWizard
          onCreated={(jt): void => {
            setWizardOpen(false);
            void (async (): Promise<void> => {
              // Plan 09.1-11 Task 2 Step 3 — close, invalidate, toast, then
              // open the flat edit form on the newly created id so the admin
              // can immediately review/adjust.
              await invalidate();
              toast.success('Job type created');
              startEdit(jt);
            })();
          }}
          onCancel={(): void => setWizardOpen(false)}
        />
      )}

      {editingId !== null && (
        <>
          <JobTypeForm
            form={form}
            setForm={setForm}
            version={originalJobType?.version ?? null}
            saving={saving}
            error={error}
            confirmState={confirmState}
            supersededConflict={supersededConflict}
            onSave={(): void => void save()}
            onCancel={cancel}
            onConfirmVersion={confirmNewVersion}
            onCancelConfirm={cancelConfirm}
            onReload={(): void => void reloadAfterConflict()}
          />
          <CustomerScopingControl jobTypeId={editingId} />
        </>
      )}

      <div style={{ marginTop: '12px' }}>
        {isLoading && <div style={subText}>Loading…</div>}
        {!isLoading && jobTypes.length === 0 && (
          <div style={{ ...rowStyle, flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ fontWeight: 600 }}>No job types yet</span>
            <span style={subText}>
              Add at least one active job type — dispatchers pick from these when creating a job,
              and this step can only be marked complete once one exists.
            </span>
          </div>
        )}
        {jobTypes.map((jt) => (
          <div key={jt.id} style={{ ...rowStyle, opacity: jt.isActive ? 1 : 0.55 }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{jt.label}</span>{' '}
              <span style={subText}>
                · {jt.code} · v{jt.version}
              </span>
              {jt.isDefaultForDispatch && <span style={subText}> · default</span>}
              {!jt.isActive && <span style={subText}> · inactive</span>}
              <ScopedBadge jobTypeId={jt.id} />
              <div style={subText}>
                {jt.billingMethod} · {jt.completionLocation}
                {jt.defaultRateCents !== null
                  ? ` · $${formatNumber(jt.defaultRateCents / 100, { decimals: 2 })}`
                  : ''}
              </div>
            </div>
            <button type="button" style={btn} onClick={(): void => startEdit(jt)}>
              Edit
            </button>
            <button
              type="button"
              style={btn}
              onClick={(): void => void setActive(jt, !jt.isActive)}
            >
              {jt.isActive ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** JOBTYPE-13 — "Scoped" marker for list rows whose family has customer
 *  scoping rows. allCustomers (the zero-rows sentinel) renders nothing. */
function ScopedBadge({ jobTypeId }: { jobTypeId: number }): React.JSX.Element | null {
  const opts = useApiOpts();
  const { data } = useJobTypeCustomers(opts, jobTypeId);
  if (data === undefined || data.allCustomers) return null;
  return <span style={subText}> · Scoped</span>;
}

/** JOBTYPE-13 — customer-scoping control inside the edit form. Empty
 *  selection renders as "All customers", never as "none" (the sentinel). */
function CustomerScopingControl({ jobTypeId }: { jobTypeId: number }): React.JSX.Element {
  const opts = useApiOpts();
  const { data: customersData, isLoading } = useJobTypeCustomers(opts, jobTypeId);
  const { data: haulerData } = useHaulerOperators();
  const replaceMutation = useReplaceJobTypeCustomers(opts, jobTypeId);
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (customersData !== undefined) {
      setSelected(customersData.customerIds);
      setDirty(false);
    }
  }, [customersData]);

  const haulerOperators = haulerData?.haulerOperators ?? [];

  const toggle = (id: string): void => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setDirty(true);
  };

  const saveScope = (): void => {
    replaceMutation.mutate(
      { customerIds: selected },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success('Customer scoping saved');
        },
      },
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '12px',
        marginTop: '10px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--color-bg-secondary)',
      }}
    >
      <div style={{ fontWeight: 600 }}>Customer scoping</div>
      <div style={subText}>
        {selected.length === 0
          ? 'All customers'
          : `${String(selected.length)} customer(s) selected`}
      </div>
      {isLoading && <div style={subText}>Loading…</div>}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          maxHeight: '160px',
          overflowY: 'auto',
        }}
      >
        {haulerOperators.map((op) => (
          <label
            key={op.id}
            style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}
          >
            <input
              type="checkbox"
              checked={selected.includes(op.id)}
              onChange={(): void => toggle(op.id)}
            />
            {op.resolvedName}
          </label>
        ))}
      </div>
      <div>
        <button
          type="button"
          style={btn}
          disabled={!dirty || replaceMutation.isPending}
          onClick={saveScope}
        >
          {replaceMutation.isPending ? 'Saving…' : 'Save customer scope'}
        </button>
      </div>
    </div>
  );
}

function JobTypeForm({
  form,
  setForm,
  version,
  saving,
  error,
  confirmState,
  supersededConflict,
  onSave,
  onCancel,
  onConfirmVersion,
  onCancelConfirm,
  onReload,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  version: number | null;
  saving: boolean;
  error: string | null;
  confirmState: ConfirmState | null;
  supersededConflict: boolean;
  onSave: () => void;
  onCancel: () => void;
  onConfirmVersion: () => void;
  onCancelConfirm: () => void;
  onReload: () => void;
}): React.JSX.Element {
  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // DEC-176 — the tenant product catalog for the Default product select.
  const { data: productsData } = useProducts();
  const products = productsData?.products ?? [];
  const hasDefaultProduct = form.defaultProductId !== '';

  /** Set the default product AND sync the unit from it (read-only while linked; the
   *  server re-derives authoritatively). Clearing it re-opens the unit for editing. */
  const setDefaultProduct = (productId: string): void => {
    setForm((prev) => ({
      ...prev,
      defaultProductId: productId,
      unitOfMeasureCode: syncedUnitForProduct(products, productId, prev.unitOfMeasureCode),
    }));
  };

  const checkbox = (key: keyof FormState, label: string, help?: string): React.JSX.Element => (
    <div>
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
        <input
          type="checkbox"
          checked={form[key] as boolean}
          onChange={(e): void => set(key, e.target.checked as FormState[typeof key])}
        />
        {label}
      </label>
      {help !== undefined && <div style={{ ...subText, marginLeft: '24px' }}>{help}</div>}
    </div>
  );

  const levelSelect = (key: keyof FormState, label: string, help?: string): React.JSX.Element => (
    <FormRow label={label} htmlFor={`jt-${key}`}>
      <>
        <select
          id={`jt-${key}`}
          value={form[key] as RequirementLevel}
          onChange={(e): void => set(key, e.target.value as FormState[typeof key])}
          style={fieldStyle}
        >
          {REQUIREMENT_LEVELS.map((lvl) => (
            <option key={lvl} value={lvl}>
              {REQUIREMENT_LABELS[lvl]}
            </option>
          ))}
        </select>
        {help !== undefined && <div style={subText}>{help}</div>}
      </>
    </FormRow>
  );

  const isTank = form.ticketShape === 'TANK';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '12px',
        marginTop: '10px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--color-bg-secondary)',
      }}
    >
      <div style={{ fontWeight: 600 }}>
        Edit job type
        {version !== null && <span style={subText}> · v{version}</span>}
      </div>

      <div style={sectionHeading}>Identity</div>
      <FormRow label="Code" htmlFor="jt-code">
        <input id="jt-code" value={form.code} disabled style={fieldStyle} />
      </FormRow>
      <FormRow label="Label" htmlFor="jt-label" required>
        <input
          id="jt-label"
          value={form.label}
          onChange={(e): void => set('label', e.target.value)}
          style={fieldStyle}
        />
      </FormRow>

      <div style={sectionHeading}>Ticket shape</div>
      <FormRow label="Ticket shape" htmlFor="jt-ticket-shape">
        <>
          <select
            id="jt-ticket-shape"
            value={form.ticketShape}
            onChange={(e): void => {
              const next = e.target.value as TicketShape;
              set('ticketShape', next);
              // Phase 12.1 (D-06) — LINE_ITEM always bills PER_UNIT; migration
              // 0075's DB CHECK rejects any other billing_method for a
              // LINE_ITEM row, so lock it the instant the shape flips.
              if (next === 'LINE_ITEM') set('billingMethod', 'PER_UNIT');
            }}
            style={fieldStyle}
          >
            {TICKET_SHAPES.map((s) => (
              <option key={s} value={s}>
                {s === 'TANK' ? 'Tank-based' : 'Line-item'}
              </option>
            ))}
          </select>
          <div style={subText}>Structural — changing this creates a new version.</div>
          {form.ticketShape === 'LINE_ITEM' && (
            <div style={subText}>
              Line-item tickets can be configured now; the line-item capture screen ships in a later
              phase.
            </div>
          )}
        </>
      </FormRow>

      {isTank && (
        <>
          <div style={sectionHeading}>Tank list</div>
          <FormRow label="Tank count mode" htmlFor="jt-tank-count-mode">
            <>
              <select
                id="jt-tank-count-mode"
                value={form.tankCountMode}
                onChange={(e): void => set('tankCountMode', e.target.value as TankCountMode)}
                style={fieldStyle}
              >
                {TANK_COUNT_MODES.map((m) => (
                  <option key={m} value={m}>
                    {TANK_COUNT_MODE_LABELS[m]}
                  </option>
                ))}
              </select>
              <div style={subText}>Structural — changing this creates a new version.</div>
            </>
          </FormRow>
          {form.tankCountMode === 'MAX_N' && (
            <FormRow label="Max tank count" htmlFor="jt-tank-count-max">
              <input
                id="jt-tank-count-max"
                inputMode="numeric"
                value={form.tankCountMax}
                placeholder="e.g. 4"
                onChange={(e): void => set('tankCountMax', e.target.value)}
                style={fieldStyle}
              />
            </FormRow>
          )}

          <div style={sectionHeading}>Capture fields</div>
          {levelSelect('topGaugeRequirement', 'Top gauge')}
          {levelSelect('bottomGaugeRequirement', 'Bottom gauge')}
          {levelSelect('waterVolumeRequirement', 'Water volume')}
          {levelSelect('oilVolumeRequirement', 'Oil volume')}
          {levelSelect('sealRequirement', 'Seal info')}
          {levelSelect(
            'colorCutRequirement',
            'Color cut',
            'Predefined option list is still pending real hauler input (OPEN-001) — only the mechanism ships here.',
          )}
          {levelSelect(
            'tankNameRequirement',
            'Tank name',
            "Auto-filled from a registered tank when linked; otherwise the driver's entry; otherwise Tank 1, Tank 2, …",
          )}
          {/* DEC-176 — require-product rule. Only shown when there is NO default
              product (with a default, every tank is pre-filled, so there is no
              "unselected" state to guard). Optional/Required only (no Hidden — the
              per-tank product picker is always available). */}
          {!hasDefaultProduct && (
            <FormRow label="Product" htmlFor="jt-product-req">
              <>
                <select
                  id="jt-product-req"
                  value={form.productRequirement}
                  onChange={(e): void =>
                    set('productRequirement', e.target.value as RequirementLevel)
                  }
                  style={fieldStyle}
                >
                  <option value="OPTIONAL">Optional</option>
                  <option value="REQUIRED">Required</option>
                </select>
                <div style={subText}>
                  With no default product, require the driver to choose a product per tank before
                  completing (e.g. a Cellars job type).
                </div>
              </>
            </FormRow>
          )}
        </>
      )}

      <div style={sectionHeading}>Driver requirements</div>
      {levelSelect('driverCommentRequirement', 'Driver comment')}
      {levelSelect('photoRequirement', 'Photo', PHOTO_REQUIREMENT_HELP)}
      {levelSelect(
        'driverTimeRequirement',
        'Driver time',
        'Total time is confirmed by the driver at completion; elapsed time is derived from the timeclock event chain.',
      )}

      <div style={sectionHeading}>Billing</div>
      <FormRow label="Billing method" htmlFor="jt-billing">
        {form.ticketShape === 'LINE_ITEM' ? (
          // Phase 12.1 (D-06) — LINE_ITEM always bills PER_UNIT (locked by
          // migration 0075's DB CHECK); render read-only, not an editable
          // dropdown, so the invalid combination can't be constructed.
          <div style={fieldStyle}>
            <span>PER_UNIT</span>
            <span style={{ ...subText, marginLeft: '8px' }}>
              (Line-item job types always bill per unit)
            </span>
          </div>
        ) : (
          <select
            id="jt-billing"
            value={form.billingMethod}
            onChange={(e): void => set('billingMethod', e.target.value)}
            style={fieldStyle}
          >
            {BILLING_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        )}
      </FormRow>
      <FormRow label="Standard rate ($)" htmlFor="jt-rate">
        <input
          id="jt-rate"
          inputMode="decimal"
          value={form.rateDollars}
          placeholder="optional"
          onChange={(e): void => set('rateDollars', e.target.value)}
          style={fieldStyle}
        />
        <div style={subText}>
          Used when the customer has no price for this job type and no general rate.
        </div>
      </FormRow>
      {/* DEC-176 — default product. Structural: changing it forks a new version. */}
      <FormRow label="Default product" htmlFor="jt-default-product">
        <>
          <select
            id="jt-default-product"
            value={form.defaultProductId}
            onChange={(e): void => setDefaultProduct(e.target.value)}
            style={fieldStyle}
          >
            <option value="">— None (choose per tank on site) —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.unitOfMeasure ? ` (${p.unitOfMeasure})` : ''}
              </option>
            ))}
          </select>
          <div style={subText}>
            Pre-fills each tank&apos;s product on the run ticket (drivers can still override).
            Structural — changing it creates a new version.
          </div>
        </>
      </FormRow>
      <FormRow label="Unit of measure" htmlFor="jt-uom">
        <>
          <input
            id="jt-uom"
            value={form.unitOfMeasureCode}
            placeholder="BBL"
            disabled={hasDefaultProduct}
            onChange={(e): void => set('unitOfMeasureCode', e.target.value)}
            style={fieldStyle}
          />
          {hasDefaultProduct && (
            <div style={subText}>Set by the default product — change it by picking a product.</div>
          )}
        </>
      </FormRow>
      {checkbox(
        'useCompanyPricing',
        'Use company pricing',
        "Uses this customer's rate when one is set; otherwise falls back to the rate above.",
      )}
      {checkbox(
        'disposalFeeEnabled',
        'Add a disposal fee when billing',
        'Fee amounts are configured in Billing.',
      )}

      <div style={sectionHeading}>Other</div>
      <FormRow label="Capture method" htmlFor="jt-capture">
        <>
          <select
            id="jt-capture"
            value={form.captureMethod}
            onChange={(e): void => set('captureMethod', e.target.value)}
            style={fieldStyle}
          >
            {CAPTURE_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <div style={subText}>Applies within tank-based tickets.</div>
        </>
      </FormRow>
      <FormRow label="Completion location" htmlFor="jt-completion">
        <>
          <select
            id="jt-completion"
            value={form.completionLocation}
            onChange={(e): void => set('completionLocation', e.target.value)}
            style={fieldStyle}
          >
            {COMPLETION_LOCATIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <div style={subText}>
            Controls where the job ends. Billing&apos;s disposal fee is a separate setting.
          </div>
        </>
      </FormRow>
      {checkbox('isServiceWork', 'Service Work?')}
      {checkbox('requireSignatureReason', 'Require reason when no signature')}
      {checkbox('isDefaultForDispatch', 'Default selection in New Job form')}
      {checkbox('isActive', 'Active')}

      {error !== null && (
        <div style={{ fontSize: '13px', color: 'var(--color-danger)' }}>{error}</div>
      )}

      {confirmState !== null && (
        <div style={confirmBoxStyle}>
          <div style={{ fontWeight: 600 }}>
            This creates a new version of &quot;{form.label}&quot;
          </div>
          <div style={subText}>
            You changed:{' '}
            {confirmState.changedFields.map((f) => STRUCTURAL_FIELD_LABELS[f] ?? f).join(', ')}.
          </div>
          <div style={subText}>
            {confirmState.jobCount === 0
              ? 'No existing tickets use this job type.'
              : `${formatNumber(confirmState.jobCount)} existing ticket(s) will keep using version ${String(confirmState.version)} and are unaffected. New tickets will use version ${String(confirmState.version + 1)}.`}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <button type="button" style={btn} disabled={saving} onClick={onCancelConfirm}>
              Cancel
            </button>
            <button type="button" style={primaryBtn} disabled={saving} onClick={onConfirmVersion}>
              {saving ? 'Creating…' : 'Create new version'}
            </button>
          </div>
        </div>
      )}

      {supersededConflict && (
        <div style={{ ...confirmBoxStyle, borderColor: 'var(--color-danger)' }}>
          <button type="button" style={primaryBtn} onClick={onReload}>
            Reload
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        <button
          type="button"
          style={primaryBtn}
          disabled={saving || confirmState !== null}
          onClick={onSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" style={btn} disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
